import { LoadingButton } from '@mui/lab';
import {
  Grid,
  MenuItem,
  Select,
  Typography,
  LinearProgress,
  IconButton,
  CardMedia,
  Hidden,
  Badge,
  Tooltip,
  Theme,
} from '@mui/material';
import { Box } from '@mui/system';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  Distributor,
  getDistributorsList,
  // getDistributorsListForFilters,
  getRMsList,
} from '../../redux-store/actions';
import {
  fundAccess,
  // getFundsList
} from '../../redux-store/actions/funds';
import {
  distributorOnboardingCompletedDownload,
  distributorWiseCommitmentAmountDownload,
  distributorWiseOnBoardingSummaryDownload,
  getCompletedApplications,
  getDistributorWiseCommitmentAmount,
  getDistributorWiseOnBoardingSummary,
  getOnBoardingDetails,
  getOnBoardingSummary,
  onBoardingDetailsDownload,
  onBoardingDetailsDownloadAllFields,
  onBoardingSummaryDownload,
} from '../../redux-store/actions/reports';
import {
  DistributorCommitmentAmountReportTypeSummary,
  GenericReportTypeSummary,
  GetDistributorCommitmentAmount,
  GetOnboardingCompletedDetailsReportResponse,
  GetOnboardingDetailsReportResponse,
  GetOnBoardingSummaryReportResponse,
  OnBoardingCompletedReportDetailsType,
  OnBoardingReportDetailsType,
  ReportFilterType,
  RMType,
} from '../../redux-store/types/api-types';
import {
  accessType,
  //  FundProps, GetFundsListResponseBody
} from '../../redux-store/types/funds';
import { CommonLayout } from '../commonComponents';
import { ColumnType, DataTable } from '../DataTable';
import { FilterDatePicker, FilterDropdown, initialFilters } from './filters';
import {
  applicationTypes,
  getFromDate,
  onboardingDetailsDownload,
  reportData,
  reportDownload,
} from './report-utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '../../lib/formik/DatePicker';
import dayjs from '../../utils/dayjs';
import { USER_ROLES, WEB_NAME } from '../../utils/constant';
import { RootStateType } from '../../redux-store/reducers';
import BreadCrum from '../../lib/BreadCrum';
import { Form, Formik } from '../../lib/formik/Formik';
import { DayJsDatePicker } from '../onboarding/newDatePicker';
import { isModuleAllowed } from '../../utils/utilityFunctions';

const tableMainBoxStyles = {
  maxHeight: 'calc(100vh - 240px)',
  minHeight: '150px',
};


const CommonComponentForSummaryReports = ({
  isLoading,
  tableHeaders,
  summaryReport,
}: {
  isLoading: boolean;
  tableHeaders: ColumnType[];
  summaryReport: GetOnBoardingSummaryReportResponse[];
}): JSX.Element => {
  const getApplicationStatusCount = (
    applications: GenericReportTypeSummary[],
    status: string
  ): string => {
    return applications.find((application) => application.status === status)?.count || '0';
  };

  return (
    <Box>
      {isLoading ? (
        <LinearProgress />
      ) : (
        summaryReport.map((report) => {
          const tableData = Object.keys(report)
            .filter((key) => key !== 'name')
            .map((key) => {
              const applicationType = applicationTypes
                .map((type) => {
                  if (type.key === key) {
                    return type.value;
                  }
                  return;
                })
                .filter((ele) => ele)
                .toString();
              const applications = report[key] as GenericReportTypeSummary[];
              if (applications.length) {
                return {
                  investorType: applicationType,
                  total_onboarded: getApplicationStatusCount(applications, 'total_onboarded'),
                  in_progress: getApplicationStatusCount(applications, 'in_progress'),
                  draft: getApplicationStatusCount(applications, 'draft'),
                  pending_with_investor: getApplicationStatusCount(
                    applications,
                    'pending_with_investor'
                  ),
                  pending_with_im: getApplicationStatusCount(
                    applications,
                    'pending_with_fundmanager'
                  ),
                  pending_with_trustee: getApplicationStatusCount(
                    applications,
                    'pending_with_trustee'
                  ),
                  invitationexpired: getApplicationStatusCount(applications, 'invitationexpired'),
                  rejected: getApplicationStatusCount(applications, 'rejected'),
                  completed: getApplicationStatusCount(applications, 'completed'),
                };
              }
              return;
            })
            .filter((ele) => ele);
          return (
            <>
              <Typography key={report.name} mt={2}>
                {report.name}
              </Typography>
              <Box sx={{ paddingTop: '20px', paddingBottom: '40px' }}>
                <DataTable
                  isLoading={false}
                  tableHeader={tableHeaders}
                  tableData={tableData}
                  boxStyles={tableMainBoxStyles}
                />
              </Box>
            </>
          );
        })
      )}
    </Box>
  );
};

const OnBoardingSummaryReport = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetOnBoardingSummaryReportResponse[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getOnBoardingSummary(filters)
        )) as unknown as GetOnBoardingSummaryReportResponse[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <CommonComponentForSummaryReports
      isLoading={isLoading}
      tableHeaders={tableHeaders}
      summaryReport={summaryReport}
    />
  );
};

const OnBoardingDetailsReport = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<OnBoardingReportDetailsType[]>([]);
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(1);

  const apiCall = async (page: number) => {
    setIsLoading(true);
    const { data = [], pages = 0 } = (await dispatch(
      getOnBoardingDetails({ ...filters, page, limit: 10 })
    )) as unknown as GetOnboardingDetailsReportResponse;
    return { data, pages };
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        const { data, pages } = await apiCall(1);
        if (!isComponentActive) return;
        setTableData(data);
        setNoPages(pages);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  const handlePageChange = async (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const { data, pages } = await apiCall(value);
    setTableData(data);
    setNoPages(pages);
    setIsLoading(false);
  };

  return (
    <DataTable
      isLoading={isLoading}
      tableHeader={tableHeaders}
      tableData={tableData}
      boxStyles={tableMainBoxStyles}
      noOfPages={noOfPages}
      currentPage={currentPage}
      paginationAction={handlePageChange}
    />
  );
};

const DistributorOnBoardingSummary = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetOnBoardingSummaryReportResponse[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getDistributorWiseOnBoardingSummary(filters)
        )) as unknown as GetOnBoardingSummaryReportResponse[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <CommonComponentForSummaryReports
      isLoading={isLoading}
      tableHeaders={tableHeaders}
      summaryReport={summaryReport}
    />
  );
};

const DistributorCommitmentAmount = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetDistributorCommitmentAmount[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getDistributorWiseCommitmentAmount(filters)
        )) as unknown as GetDistributorCommitmentAmount[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <>
      {' '}
      <Typography
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}>
        <InfoOutlinedIcon color="info" fontSize="small" />
        <Typography
          sx={{
            fontSize: 15,
            ml: 1,
          }}>
          Data is shown only for completed applications .
        </Typography>
      </Typography>
      {isLoading ? (
        <LinearProgress />
      ) : (
        summaryReport.map((report) => {
          const tableData = Object.keys(report)
            .filter((key) => key !== 'name')
            .map((key) => {
              const applicationType = applicationTypes
                .map((type) => {
                  if (type.key === key) {
                    return type.value;
                  }
                  return;
                })
                .filter((ele) => ele)
                .toString();
              const applications = report[key] as DistributorCommitmentAmountReportTypeSummary;
              if (Object.keys(report[key]).length === 0) {
                return;
              }
              return {
                investorType: applicationType,
                completed: applications.completed,
                commitmentAmount: applications.commitmentAmount,
                contributionAmount: applications.contributionAmount,
              };
            })
            .filter((ele) => ele);
          return (
            <>
              <Typography key={report.name} mt={2}>
                {report.name}
              </Typography>
              <Box sx={{ paddingTop: '20px', paddingBottom: '40px' }}>
                <DataTable
                  isLoading={false}
                  tableHeader={tableHeaders}
                  tableData={tableData}
                  boxStyles={tableMainBoxStyles}
                />
              </Box>
            </>
          );
        })
      )}
    </>
  );
};

const OnBoardingCompletedDetailsReport = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<OnBoardingCompletedReportDetailsType[]>([]);
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(1);

  const apiCall = async (page: number) => {
    setIsLoading(true);
    const { data = [], pages = 0 } = (await dispatch(
      getCompletedApplications({ ...filters, page })
    )) as unknown as GetOnboardingCompletedDetailsReportResponse;
    return { data, pages };
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        const { data, pages } = await apiCall(1);
        if (!isComponentActive) return;
        setTableData(data);
        setNoPages(pages);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  const handlePageChange = async (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const { data, pages } = await apiCall(value);
    setTableData(data);
    setNoPages(pages);
    setIsLoading(false);
  };

  return (
    <>
      {' '}
      <Typography
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}>
        <InfoOutlinedIcon color="info" fontSize="small" />
        <Typography
          sx={{
            fontSize: 15,
            ml: 1,
            mb: 2,
          }}>
          Data is shown only for completed applications .
        </Typography>
      </Typography>
      <DataTable
        isLoading={isLoading}
        tableHeader={tableHeaders}
        tableData={tableData}
        boxStyles={tableMainBoxStyles}
        noOfPages={noOfPages}
        currentPage={currentPage}
        paginationAction={handlePageChange}
      />
    </>
  );
};

const ReportsLayout = ({ children }: any) => {
  const { role = '' } = useSelector((state: RootStateType) => state.auth);
  // return role === USER_ROLES.AMCAPPROVER ? (
  //   <CommonLayoutForPOA>
  //     {children}
  //   </CommonLayoutForPOA>
  // ) : (
  //   <CommonLayout>
  //     {children}
  //   </CommonLayout>
  // )

  return (
    <CommonLayout>
      {children}
    </CommonLayout>
  )
}
export default function Reports(): JSX.Element {
  const dispatch = useDispatch();
  const [reportType, setReportType] = useState(reportData[0].value);
  const [filters, setFilters] = useState<ReportFilterType>(initialFilters);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<boolean>(false);
  const open = Boolean(filtersAnchorEl);
  // const [funds, setFunds] = useState<FundProps[]>([]);
  // const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [toDateErrorText, setToDateErrorText] = useState('');
  const [fromDateErrorText, setFromDateErrorText] = useState('');
  const [rmsList, setRmList] = useState<RMType[]>([]);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [access, setAccess] = useState(false);
  const currentDate = dayjs();

  const { role = '', permissions = [] } = useSelector((state: RootStateType) => state.auth);

  const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
    {
      name: 'Dashboard',
      path: '/dashboard',
    },
    {
      name: `Reports`,
      path: '/reports',
    },
  ] : [])

  const validationSchema = Yup.object().shape({
    fromDate: Yup
      .date()
      .required('From Date is required')
      .typeError('From Date is required')

      // .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
      .test('min-date', "From Date should not be less than 01/01/1950", function (value) {
        if (!value) return true;
        return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
      })

      // .max(currentDate, "From Date should not be greater than today's date")
      .test('max-today', "From Date should not be greater than today's date", function (value) {
        if (!value) return true;
        return dayjs(value).isSameOrBefore(dayjs(currentDate), 'day');
      })
      .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
        // const { toDate } = this.parent;
        // return !toDate || !value || value <= toDate;

        const { toDate } = this.parent;
        if (!toDate || !value) return true;
        return dayjs(value).isSameOrBefore(dayjs(toDate), 'day');
      }),
    toDate: Yup
      .date()
      .required('To Date is required')
      .typeError('To Date is required')

      // .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
      .test('min-date', "To Date should not be less than 01/01/1950", function (value) {
        if (!value) return true;
        return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
      })

      // .max(currentDate, "To Date should not be greater than today's date")
      .test('max-today', "To Date should not be greater than today's date", function (value) {
        if (!value) return true;
        return dayjs(value).isSameOrBefore(dayjs(currentDate), 'day');
      })
      .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
        // const { fromDate } = this.parent;
        // return !fromDate || !value || value >= fromDate;

        const { fromDate } = this.parent;
        if (!fromDate || !value) return true;
        return dayjs(value).isSameOrAfter(dayjs(fromDate), 'day');
      }),
  })

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        //super Admin Access same for creating funds and viewing completed applications report
        // const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        // setAccess(fundSetupAccess);
        // const { funds } = (await dispatch(getFundsList())) as unknown as GetFundsListResponseBody;
        // const response = (await dispatch(
        //   getDistributorsListForFilters()
        // )) as unknown as Distributor[];
        const rms = (await dispatch(getRMsList())) as unknown as RMType[];
        if (!isComponentActive) return;
        // setFunds(funds);
        // setDistributors(response);
        setRmList(rms);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  // useEffect(() => {
  //   setFilters(initialFilters);
  // }, [reportType]);

  const reportsTable = useMemo(() => {
    const tableHeaders = reportData.find(({ value }) => value === reportType)?.tableHeaders || [];
    const updatedFilter = { ...filters, status: filters?.status?.toString() };
    switch (reportType) {
      case 'onboardingSummary':
        return <OnBoardingSummaryReport tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'onboardingDetails':
        return <OnBoardingDetailsReport tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'distributorOnBoardingSummary':
        return <DistributorOnBoardingSummary tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'distributorCommitmentAmount':
        return <DistributorCommitmentAmount tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'onboardingCompletedDetails':
        return (
          <OnBoardingCompletedDetailsReport tableHeaders={tableHeaders} filters={updatedFilter} />
        );
    }
  }, [reportType, filters]);

  const handleFiltersAnchorEl = () => {
    setFiltersAnchorEl(true);
  };

  const handleFiltersAnchorElClose = () => {
    setFiltersAnchorEl(false);
  };

  const filterOptions = (fromDate: string, toDate: string): JSX.Element => {
    const badgeCount = [
      'distributorId',
      // 'schemeId',
      // 'planId',
      'rmId',
      // 'investorType',
    ].filter((filterKey) => filters[filterKey]).length;
    const updatedBadgeCountWithStatusFilter =
      filters.status?.length > 0 ? badgeCount + 1 : badgeCount;
    return (
      <>
        <IconButton
          sx={{ p: 0.5 }}
          id="filter-icon-button"
          aria-controls="filter-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleFiltersAnchorEl}>
          <Badge badgeContent={updatedBadgeCountWithStatusFilter} color="info">
            <CardMedia component="img" image="/images/filter-icon.svg" alt="Filter Icon" />
          </Badge>
        </IconButton>{' '}
        {filtersAnchorEl && (
          <FilterDropdown
            filters={{ ...filters, from: fromDate, to: toDate }}
            onFiltersChange={(value) => setFilters(value)}
            anchorEl={filtersAnchorEl}
            handleClose={handleFiltersAnchorElClose}
            // funds={funds}
            // distributors={distributors}
            rmsList={rmsList}
            onboardingReports={true}
          />
        )}
      </>
    );
  };

  const handleDownloadReport = async (values: { fromDate: string; toDate: string }): Promise<any> => {
    try {
      setIsSendLoading(true);
      const download = { ...filters, from: dayjs(values.fromDate)?.toISOString(), to: dayjs(values.toDate)?.toISOString(), downloadFile: true };
      setFilters({ ...filters, from: dayjs(values.fromDate)?.toISOString(), to: dayjs(values.toDate)?.toISOString() });
      const res = await dispatch(
        onBoardingDetailsDownloadAllFields(download)
      );
      reportDownload(
        res as unknown as Blob,
        `${WEB_NAME}-onboarding-details.xlsx`
      );
    } catch (e) {
      setIsSendLoading(false);
      console.error((e as Error).message);
    } finally {
      setIsSendLoading(false);
    }
  }






  return (
    <>
      <ReportsLayout>
        <>
          <Formik
            initialValues={{ fromDate: dayjs(getFromDate()).format("YYYY-MM-DD"), toDate: dayjs(currentDate).format("YYYY-MM-DD"), }}
            validationSchema={validationSchema}
            onSubmit={handleDownloadReport}
          >
            {({ values }) => (
              <Form>
                <Box>
                  <Box>
                    {/* {([USER_ROLES.RM, USER_ROLES.OPE, USER_ROLES.OPM].includes(role)) && (
                      <Box sx={{ mb: 2 }}>
                        <BreadCrum array={rmRouterPath} />
                      </Box>
                    )} */}
                    {/* {(USER_ROLES.AMC_ADMIN === role) && ( */}
                    <Typography
                      variant='h1'
                      sx={{
                        alignSelf: { xs: 'center' },
                        mb: 3
                      }}>
                      Reports
                    </Typography>
                    {/* )} */}
                    <Grid container alignItems={'baseline'} spacing={3}>
                      <Grid item xs={10} sm={10} md={2}>
                        {/* <Select
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 10px',
                      borderRadius: '6px',
                      fontSize: '15px',
                    }}
                    value={reportType}
                    onChange={({ target: { value } }) => {
                      // setFilters(initialFilters);
                      setReportType(value);
                    }}>
                    {reportData
                      .filter((reports) => {
                        if (!access && reports.value === 'onboardingCompletedDetails') {
                          return;
                        }
                        return reports;
                      })
                      .filter((ele) => ele)
                      .map((report) => (
                        <MenuItem value={report.value} key={report.key}>
                          {report.key}
                        </MenuItem>
                      ))}
                  </Select> */}

                        {/* <Typography variant='body_700' textAlign={([USER_ROLES.RM, USER_ROLES.OPE, USER_ROLES.OPM].includes(role)) ? 'start' : 'center'}>Onboarding Details</Typography> */}
                        <Typography variant='body_700'>Onboarding Details</Typography>
                      </Grid>
                      {/* <Hidden mdUp={true}>
                  <Grid item xs={2}>
                    {filterOptions()}
                  </Grid>
                </Hidden> */}
                      <Grid item xs={6} sm={6} md={5} display={'flex'} alignItems={'center'} columnGap={2}>
                        {/* <Typography variant='body_400' sx={(theme: Theme) => ({ color: theme.palette.text.primary, whiteSpace: 'nowrap' })}>From Date</Typography> */}
                        {/* <FilterDatePicker
                    placeholder={'From Date'}
                    onChange={(date) => {
                      if (!date || !dayjs(date).isValid()) {
                        // Handle the case where the date is invalid or not set yet
                        setFromDateErrorText('Please select a valid date');
                        return;
                      }
                      const modifiedDate = date?.toISOString();
                      const timeDiff =
                        new Date(modifiedDate as Date).getTime() - new Date(filters.to).getTime();
                      if (timeDiff > 0) {
                        setFromDateErrorText('From Date should be less than To Date');
                        return;
                      }
                      setFilters({ ...filters, from: new Date(modifiedDate as Date).toISOString() });
                      setFromDateErrorText('');
                    }}
                    // value={new Date(filters.from)}
                    value={dayjs(filters.from)}
                    errorText={fromDateErrorText}
                  /> */}
                        <DayJsDatePicker
                          id=""
                          name='fromDate'
                          label="From Date"
                          maxDate={new Date()}
                          currentDate={filters.from}
                          styleObj={(theme: Theme) => ({
                            backgroundColor: theme.palette.primary[50],
                            borderRadius: "10px",
                            '&.MuiTextField-root': {
                              '& fieldset': {
                                border: `0.5px solid ${theme.palette.primary[50]}`,
                              },
                              '.MuiInputBase-root': {
                                '.MuiInputBase-input': {
                                  ...theme.typography.body_500,
                                  letterSpacing: "0.32px",
                                  color: theme.palette.primary[500],
                                  padding: "15px"
                                },
                              }
                            },
                          })}
                          resetDate
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={5} display={'flex'} alignItems={'center'} columnGap={2}>
                        {/* <Typography variant='body_400' sx={(theme: Theme) => ({ color: theme.palette.text.primary, whiteSpace: 'nowrap' })}>To Date</Typography> */}
                        {/* <FilterDatePicker
                    placeholder={'To Date'}
                    // value={new Date(filters.to)}
                    value={dayjs(filters.to)}
                    onChange={(date) => {
                      if (!date || !dayjs(date).isValid()) {
                        // Handle the case where the date is invalid or not set yet
                        setFromDateErrorText('Please select a valid date');
                        return;
                      }
                      const modifiedDate = date?.toISOString();
                      const timeDiff =
                        new Date(filters.from).getTime() - new Date(modifiedDate as Date).getTime();
                      if (timeDiff > 0) {
                        setToDateErrorText('To Date should be greater than From Date');
                        return;
                      }
                      setFilters({ ...filters, to: new Date(modifiedDate as Date).toISOString() });
                      setToDateErrorText('');
                    }}
                    errorText={toDateErrorText}
                  /> */}

                        <DayJsDatePicker
                          id=""
                          name='toDate'
                          label="To Date"
                          maxDate={new Date()}
                          styleObj={(theme: Theme) => ({
                            backgroundColor: theme.palette.primary[50],
                            borderRadius: "10px",
                            '&.MuiTextField-root': {
                              '& fieldset': {
                                border: `0.5px solid ${theme.palette.primary[50]}`,
                              },
                              '.MuiInputBase-root': {
                                '.MuiInputBase-input': {
                                  ...theme.typography.body_500,
                                  letterSpacing: "0.32px",
                                  color: theme.palette.primary[500],
                                  padding: "15px"
                                },
                              }
                            },
                          })}
                          resetDate
                        />
                      </Grid>
                      {/* <Hidden only={['xs', 'sm']}>
                  <Grid item md={1}>
                    {filterOptions()}
                  </Grid>
                </Hidden> */}
                    </Grid>
                  </Box>
                  {/* <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              {reportType !== 'onboardingDetails' ? (
                <LoadingButton
                  loadingPosition="start"
                  type="submit"
                  loading={isSendLoading}
                  variant="contained"
                  sx={{
                    fontSize: 14,
                    minWidth: isSendLoading ? 180 : '',
                    letterSpacing: 1,
                    fontWeight: 400,
                    px: isSendLoading ? 0 : 3,
                    py: 1,
                    mt: 2,
                    mr: 4,
                  }}
                  onClick={async () => {
                    try {
                      setIsSendLoading(true);
                      const download = { ...filters, downloadFile: true };
                      switch (reportType) {
                        case 'onboardingSummary': {
                          const res = await dispatch(onBoardingSummaryDownload(download));
                          reportDownload(res as unknown as Blob, `${WEB_NAME}-onboarding-summary.xlsx`);
                          return;
                        }
                        case 'distributorOnBoardingSummary': {
                          const res = await dispatch(
                            distributorWiseOnBoardingSummaryDownload(download)
                          );
                          reportDownload(res as unknown as Blob, `${WEB_NAME}-onboarding-summary.xlsx`);
                          return;
                        }
                        case 'distributorCommitmentAmount': {
                          const res = await dispatch(
                            distributorWiseCommitmentAmountDownload(download)
                          );
                          reportDownload(
                            res as unknown as Blob,
                            `${WEB_NAME}-distributor-commitment-amount.xlsx`
                          );
                          return;
                        }
                        case 'onboardingCompletedDetails': {
                          const res = await dispatch(
                            distributorOnboardingCompletedDownload(download)
                          );
                          reportDownload(res as unknown as Blob, `${WEB_NAME}-onboarding-completed.xlsx`);
                          return;
                        }
                      }
                    } catch (e) {
                      setIsSendLoading(false);
                      console.error((e as Error).message);
                    } finally {
                      setIsSendLoading(false);
                    }
                  }}>
                  Download Report
                </LoadingButton>
              ) : (
                <Grid item xs={10} sm={10} md={3} sx={{ mt: 2, mr: 4 }}>
                  <Select
                    variant='outlined'
                    fullWidth
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 10px',
                      borderRadius: '6px',
                      fontSize: '15px',
                    }}
                    defaultValue={'Export'}
                    onChange={async ({ target: { value } }) => {
                      try {
                        const download = { ...filters, downloadFile: true };
                        if (value === 'Download Current Fields') {
                          const res = await dispatch(onBoardingDetailsDownload(download));
                          reportDownload(
                            res as unknown as Blob,
                            `${WEB_NAME}-onboarding-details-current-fields.xlsx`
                          );
                        } else if (value === 'Download All Fields') {
                          const res = await dispatch(
                            onBoardingDetailsDownloadAllFields(download)
                          );
                          reportDownload(
                            res as unknown as Blob,
                            `${WEB_NAME}-onboarding-details-all-fields.xlsx`
                          );
                        }
                      } catch (e) {
                        setIsSendLoading(false);
                        console.error((e as Error).message);
                      }
                    }}>
                    {onboardingDetailsDownload.map((report) => (
                      <MenuItem value={report} key={report}>
                        {report}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}
            </Box> */}

                  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'baseline' }}>
                    <LoadingButton
                      type="submit"
                      loading={isSendLoading}
                      variant="contained"
                      sx={{
                        fontSize: 14,
                        minWidth: isSendLoading ? 180 : '',
                        letterSpacing: 1,
                        fontWeight: 400,
                        px: isSendLoading ? 0 : 3,
                        py: 1,
                        mt: 2,
                        mr: 4,
                        cursor: 'pointer'
                      }}
                    >
                      <Typography variant='body_500'>Download Report</Typography>
                    </LoadingButton>
                    <Grid item md={1}>
                      {filterOptions(values.fromDate, values.toDate)}
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      mt: { sm: 4, xs: 4 },
                    }}>
                    {reportsTable}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </>
      </ReportsLayout >
    </>
  );
}
